import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import { HourlyForecast, Location } from '../types/weather';
import { calculateSunriseSunset } from '../utils/solarUtils';

interface WeatherSummaryProps {
  hourlyForecast: HourlyForecast[];
  location: Location;
}

const WeatherSummary: React.FC<WeatherSummaryProps> = ({
  hourlyForecast,
  location,
}) => {
  // Helper function to check if an hour is during nighttime (sun is down)
  const isNighttime = (hour: HourlyForecast): boolean => {
    try {
      const hourDate = new Date(hour.time);
      const dayDate = new Date(hourDate);
      dayDate.setHours(12, 0, 0, 0); // Set to noon for solar calculations

      const sunTimes = calculateSunriseSunset(
        location.lat,
        location.lon,
        dayDate
      );

      if (!sunTimes.sunrise || !sunTimes.sunset) {
        // If no sunrise/sunset (polar regions), assume it's always night for astronomical purposes
        return true;
      }

      const hourTime = hourDate.getTime();
      const sunriseTime = sunTimes.sunrise.getTime();
      const sunsetTime = sunTimes.sunset.getTime();

      // Check if hour is before sunrise or after sunset
      return hourTime < sunriseTime || hourTime > sunsetTime;
    } catch (error) {
      // If there's an error calculating solar times, include the hour to be safe
      return true;
    }
  };

  // Calculate best hours for astronomical observation
  // Conditions: <5% clouds, 0% rain, ≥15km visibility, <5 m/s wind, AND nighttime (sun is down)
  const clearHoursData = hourlyForecast.filter(
    (hour: HourlyForecast) =>
      hour.cloudCover.totalCloudCover !== null &&
      hour.windSpeed !== null &&
      hour.visibility !== null &&
      hour.visibility !== undefined &&
      hour.precipitation.precipitation !== null &&
      hour.cloudCover.totalCloudCover < 5 &&
      hour.precipitation.precipitation === 0 &&
      hour.visibility >= 15 &&
      hour.windSpeed < 5 &&
      isNighttime(hour)
  );

  const bestHours = clearHoursData.length;

  // Calculate average cloud coverage
  const cloudsWithData = hourlyForecast.filter(
    (h: HourlyForecast) => h.cloudCover.totalCloudCover !== null
  );
  const avgClouds =
    cloudsWithData.length > 0
      ? Math.round(
          cloudsWithData.reduce(
            (sum: number, hour: HourlyForecast) =>
              sum + (hour.cloudCover.totalCloudCover || 0),
            0
          ) / cloudsWithData.length
        )
      : null;

  // Calculate average rain chance
  const rainWithData = hourlyForecast.filter(
    (h: HourlyForecast) => h.precipitation.precipitationProbability !== null
  );
  const avgRainChance =
    rainWithData.length > 0
      ? Math.round(
          rainWithData.reduce(
            (sum: number, hour: HourlyForecast) =>
              sum + (hour.precipitation.precipitationProbability || 0),
            0
          ) / rainWithData.length
        )
      : null;

  // Calculate average wind speed
  const windWithData = hourlyForecast.filter(
    (h: HourlyForecast) => h.windSpeed !== null
  );
  const avgWindSpeed =
    windWithData.length > 0
      ? (
          windWithData.reduce(
            (sum: number, hour: HourlyForecast) => sum + (hour.windSpeed || 0),
            0
          ) / windWithData.length
        ).toFixed(1)
      : null;

  // Calculate average visibility
  const visibilityWithData = hourlyForecast.filter(
    (h: HourlyForecast) => h.visibility !== null
  );
  const avgVisibility =
    visibilityWithData.length > 0
      ? (
          visibilityWithData.reduce(
            (sum: number, hour: HourlyForecast) => sum + (hour.visibility || 0),
            0
          ) / visibilityWithData.length
        ).toFixed(1)
      : null;

  // Helper function to format time from ISO string
  const formatTime = (timeStr: string) => {
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return timeStr;
    }
  };

  // Helper function to format date from ISO string
  const formatDate = (timeStr: string) => {
    try {
      const date = new Date(timeStr);
      return date.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return timeStr;
    }
  };

  // Generate tooltip content for clear hours
  const generateClearHoursTooltip = () => {
    if (clearHoursData.length === 0) {
      return 'No clear hours found in the forecast period.';
    }

    // Group clear hours by astronomical night (sunset to sunrise)
    const groupedByNight: { [date: string]: HourlyForecast[] } = {};
    clearHoursData.forEach(hour => {
      try {
        const hourDate = new Date(hour.time);
        const dayDate = new Date(hourDate);
        dayDate.setHours(12, 0, 0, 0);

        const sunTimes = calculateSunriseSunset(
          location.lat,
          location.lon,
          dayDate
        );

        let nightDate = formatDate(hour.time);

        // If this hour is before sunrise, it belongs to the previous night
        if (
          sunTimes.sunrise &&
          hourDate.getTime() < sunTimes.sunrise.getTime()
        ) {
          const previousDay = new Date(dayDate);
          previousDay.setDate(previousDay.getDate() - 1);
          nightDate = formatDate(previousDay.toISOString());
        }

        if (!groupedByNight[nightDate]) {
          groupedByNight[nightDate] = [];
        }
        groupedByNight[nightDate].push(hour);
      } catch (error) {
        // Fallback to regular date grouping if solar calculation fails
        const date = formatDate(hour.time);
        if (!groupedByNight[date]) {
          groupedByNight[date] = [];
        }
        groupedByNight[date].push(hour);
      }
    });

    // Format the tooltip content as HTML for bold formatting
    let content = `<div>
      <div style="margin-bottom: 8px;">Clear Night Hours (&lt; 5% clouds, 0% rain, ≥ 15km visibility, &lt; 5 m/s wind, sun down):</div>
    `;

    Object.entries(groupedByNight).forEach(([date, hours]) => {
      // Sort hours by time to show them in chronological order
      const sortedHours = hours.sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
      );

      // Format as a bold headline
      content += `<div style="font-weight: bold; margin-top: 8px; margin-bottom: 4px;">${date} night:</div>`;

      let hasShownNextDayIndicator = false;
      sortedHours.forEach(hour => {
        const hourDate = new Date(hour.time);
        const timeStr = formatTime(hour.time);

        // Add date indicator only for the first hour that's in the next calendar day
        if (hourDate.getHours() < 12 && !hasShownNextDayIndicator) {
          const nextDay = new Date(hourDate);
          const nextDayStr = nextDay.toLocaleDateString([], {
            month: 'short',
            day: 'numeric',
          });
          content += `<div style="margin-left: 8px;">${timeStr} (${nextDayStr})</div>`;
          hasShownNextDayIndicator = true;
        } else {
          content += `<div style="margin-left: 8px;">${timeStr}</div>`;
        }
      });
    });

    content += '</div>';
    return content;
  };

  // Generate a unique tooltip ID to avoid conflicts between multiple locations
  const tooltipId = `clear-hours-tooltip-${location.lat}-${location.lon}`;

  return (
    <>
      <div className="overview-summary">
        <div className="summary-item">
          <span className="summary-label">Good Hours:</span>
          <span
            className="summary-value"
            data-tooltip-id={tooltipId}
            style={{ cursor: 'pointer' }}
          >
            {bestHours} clear hour{bestHours === 1 ? '' : 's'}
          </span>
        </div>

        <div className="summary-item">
          <span className="summary-label">Avg Clouds:</span>
          <span className="summary-value">
            {avgClouds !== null ? `${avgClouds}%` : 'N/A'}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Rain Chance:</span>
          <span className="summary-value">
            {avgRainChance !== null ? `${avgRainChance}%` : 'N/A'}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Wind Speed:</span>
          <span className="summary-value">
            {avgWindSpeed !== null ? `${avgWindSpeed} m/s` : 'N/A'}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Visibility:</span>
          <span className="summary-value">
            {avgVisibility !== null ? `${avgVisibility}km` : 'N/A'}
          </span>
        </div>
      </div>

      {/* Render tooltip via portal to ensure proper z-index layering */}
      {typeof document !== 'undefined' &&
        createPortal(
          <Tooltip
            id={tooltipId}
            place="bottom"
            offset={10}
            delayShow={200}
            delayHide={100}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
              fontSize: '0.75rem',
              maxWidth: '300px',
              zIndex: 15000, // Higher than hour tooltips (10000)
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            }}
            html={generateClearHoursTooltip()}
          />,
          document.body
        )}
    </>
  );
};

export default WeatherSummary;
